"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import SearchBar from "@/components/search/SearchBar";
import { useVehicleSelector } from "@/hooks/useVehicleSelector";
import { buildSearchUrl } from "@/lib/url-state";
import { clientSearchUrl, buildProductsQs } from "@/lib/api-url";
import { trackSearchSubmit } from "@/lib/analytics";

export default function HomeSearch() {
  const [isNavigating, setIsNavigating] = useState(false);

  // Reset spinner if user presses Back — bfcache restores frozen React state
  // including isNavigating=true, so pageshow fires and clears it.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setIsNavigating(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Prefetch: warm the data the products page will need
  useEffect(() => {
    const controller = new AbortController();
    fetch(clientSearchUrl(buildProductsQs("")), { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        try { sessionStorage.setItem("r2f_prefetch", JSON.stringify(data)); } catch {}
      })
      .catch(() => {});
    return () => { controller.abort(); };
  }, []);

  const {
    year,
    make,
    model,
    vehicleKey,
    yearOptions,
    makeOptions,
    modelOptions,
    isLoading,
    isInitialLoad,
    handleYearChange,
    handleMakeChange,
    handleModelChange,
    handleClear,
  } = useVehicleSelector();

  const navigate = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  function handleSearch(query: string) {
    trackSearchSubmit(query, "home");
    const params: Record<string, string | null> = {
      refine_search: query,
    };
    if (vehicleKey) {
      params.vehicle_key = vehicleKey;
    }
    navigate(buildSearchUrl(new URLSearchParams(), params));
  }

  // Keep a ref so handleFilterClick always reads the latest vehicleKey,
  // even if the user clicks before React re-renders after API resolves.
  const vehicleKeyRef = useRef(vehicleKey);
  useEffect(() => {
    vehicleKeyRef.current = vehicleKey;
  });

  // Auto-navigate when all 3 selections are complete (year + make + model → vehicleKey available)
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (vehicleKey) {
      handleFilterClick();
    }
  }, [vehicleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterClick = useCallback(() => {
    setIsNavigating(true);
    const currentKey = vehicleKeyRef.current;
    const href = currentKey
      ? buildSearchUrl(new URLSearchParams(), { vehicle_key: currentKey })
      : "/products";
    navigate(href);
  }, [navigate]);

  const selectClass =
    "w-full rounded border border-gray-300 bg-white px-3 py-2.5 text-sm text-black outline-none focus:border-gray-400 disabled:opacity-50";

  return (
    <>
      {/* Search Bar */}
      <div className="w-full max-w-xl px-4">
        <SearchBar
          placeholder="Search by part number, brand, description or barcode"
          onSearch={handleSearch}
        />
      </div>

      {/* Vehicle Selector Row */}
      <div className="mt-4 flex w-full max-w-xl gap-2 px-4">
        <select
          value={year}
          onChange={(e) => handleYearChange(e.target.value)}
          className={selectClass}
          disabled={isLoading}
        >
          <option value="">Year</option>
          {yearOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={make}
          onChange={(e) => handleMakeChange(e.target.value)}
          className={selectClass}
          disabled={isLoading || !year}
        >
          <option value="">Make</option>
          {makeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={model}
          onChange={(e) => handleModelChange(e.target.value)}
          className={selectClass}
          disabled={isLoading || !make}
        >
          <option value="">Model</option>
          {modelOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {vehicleKey && (
          <button
            onClick={handleClear}
            className="shrink-0 text-xs text-gray-500 hover:text-gray-700"
            aria-label="Clear vehicle"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Tagline */}
      <p className="mt-5 text-base text-gray-600">
        the right <span className="font-bold">parts</span> at the right{" "}
        <span className="font-bold">place</span> for the right{" "}
        <span className="font-bold">price</span>
      </p>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Filter Button */}
      <button
        onClick={handleFilterClick}
        disabled={isNavigating || (isLoading && !isInitialLoad)}
        className={`mb-20 flex items-center gap-2 rounded-full px-8 py-3 text-base font-medium text-white transition-all ${
          isNavigating
            ? "bg-black/70 scale-95"
            : "bg-black hover:bg-black/90 active:bg-black/80 active:scale-95"
        }`}
      >
        {isNavigating ? (
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
        )}
        {isNavigating ? "Loading..." : "Filter"}
      </button>
    </>
  );
}
